import { useCallback, useEffect } from 'react';
import {
    facebookLoginAppId as FB_APP_1_ID,
    facebookBusinessLoginAppId as FB_APP_2_ID
} from '../Config/facebookConfig';

export const facebookAppInit = async (appId) => {
    return new Promise((resolve, reject) => {
      try {
        const waitForSDK = (onReady, onTimeout) => {
          const start = Date.now();
          const check = () => {
            if (typeof window !== 'undefined' && window.FB && typeof window.FB.getLoginStatus === 'function') {
              onReady();
              return;
            }
            if (Date.now() - start > 8000) {
              onTimeout(new Error('Facebook SDK did not load in time'));
              return;
            }
            setTimeout(check, 100);
          };
          check();
        };

        waitForSDK(() => {
          // If already initialized with a different app, re-init with requested appId
          if (window.__FB_INITIALIZED__ && window.__FB_APP_ID__ && window.__FB_APP_ID__ !== appId) {
            try {
              window.FB.init({
                appId: appId,
                cookie: true,
                xfbml: true,
                version: 'v21.0',
                status: true,
                autoLogAppEvents: true,
              });
              window.__FB_APP_ID__ = appId;
            } catch (e) {
              console.error('Re-initializing Facebook SDK failed:', e);
            }
          } else if (window.__FB_INITIALIZED__) {
            // Already initialized with the same app; proceed to status check
            window.FB.getLoginStatus((response) => {
              if (response && typeof response.status === 'string') {
                resolve({
                  initialized: true,
                  authenticated: response.status === 'connected',
                  authResponse: response.authResponse,
                });
              } else {
                resolve({ initialized: true, authenticated: false });
              }
            });
            return;
          }

          // Initialize with provided app id
          window.FB.init({
            appId: appId,
            cookie: true,
            xfbml: true,
            version: 'v21.0',
            status: true,
            autoLogAppEvents: true,
          });
          window.__FB_INITIALIZED__ = true;
          window.__FB_APP_ID__ = appId;

          // Verify initialization
          window.FB.getLoginStatus((response) => {
            if (response && response.status === 'unknown') {
              resolve({ initialized: true, authenticated: false });
            } else {
              resolve({
                initialized: true,
                authenticated: response?.status === 'connected',
                authResponse: response?.authResponse,
              });
            }
          });
        }, (timeoutErr) => {
          console.error('Facebook SDK load timeout:', timeoutErr);
          reject({ initialized: false, error: timeoutErr.message });
        });
      } catch (error) {
        console.error('Facebook initialization error:', error);
        reject({ initialized: false, error: error.message });
      }
    });
  };

export const useFacebookSDK = (appNumber = 1) => {
    const switchFacebookApp = useCallback(async (appNumber) => {
        try {
            await facebookAppInit(appNumber === 1 ? FB_APP_1_ID : FB_APP_2_ID);
            return true;
        } catch (error) {
            console.error('Error switching Facebook app:', error);
            return false;
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const initSDK = async () => {
            try {
                if (isMounted) {
                    await switchFacebookApp(appNumber);
                }
            } catch (error) {
                console.error('Error initializing Facebook SDK:', error);
            }
        };

        initSDK();

        return () => {
            isMounted = false;
        };
    }, [appNumber, switchFacebookApp]);

    return switchFacebookApp;
};
